// Bind to measured cells only: "*" is an allow-list wipe of every other variant.
import type {
  RestrictionPlacement,
  StudioItem,
  StudioPlacement,
  StudioRunGeometry,
} from "./studioTypes";

// Grazing a bay line still belongs to the bay the stretch fills, not the next one.
const BAY_EDGE_EPSILON_CM = 1.0;

/** Bay numbers are run-local; multi-run rows need offsets. */
export interface BayNumbering {
  bayOffsets?: Readonly<Record<number, number>>;
}

const baysOfPlacement = (
  placement: StudioPlacement,
  run: StudioRunGeometry | undefined,
  offset: number,
): number[] => {
  if (!run || run.bayWidthCm <= 0 || run.bays <= 0) return [];
  const lastIndex = run.bays - 1;
  const clamp = (index: number) => Math.min(Math.max(index, 0), lastIndex);
  const first = clamp(Math.floor(placement.xCm / run.bayWidthCm));
  const rightEdge = Math.max(
    placement.xCm,
    placement.xCm + placement.widthCm - BAY_EDGE_EPSILON_CM,
  );
  const last = clamp(Math.floor(rightEdge / run.bayWidthCm));
  const bays: number[] = [];
  for (let index = first; index <= last; index += 1) bays.push(offset + index + 1);
  return bays;
};

/** Null means do not upload — never widen to "*". */
export const deriveRestrictionPlacement = (
  skuIds: readonly string[],
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  numbering: BayNumbering = {},
): RestrictionPlacement | null => {
  if (skuIds.length === 0) return null;
  const itemsBySku = new Map(items.map((item) => [item.skuId, item]));
  const runsByIndex = new Map(runs.map((run) => [run.run, run]));
  const offsets = numbering.bayOffsets ?? {};

  const touched = new Set(
    skuIds.flatMap((skuId) =>
      (itemsBySku.get(skuId)?.placements ?? []).map((placement) => placement.run),
    ),
  );
  if (touched.size > 1 && !numbering.bayOffsets) return null;

  // Bays are collected PER SHELF: a ragged right edge means a union would name
  // cells the value does not occupy.
  const baysByShelf = new Map<number, Set<number>>();
  for (const skuId of skuIds) {
    const item = itemsBySku.get(skuId);
    if (!item) continue;
    for (const placement of item.placements) {
      const covered = baysOfPlacement(
        placement,
        runsByIndex.get(placement.run),
        offsets[placement.run] ?? 0,
      );
      // An unresolvable stretch contributes NOTHING, never a wildcard.
      if (covered.length === 0) continue;
      const onShelf = baysByShelf.get(placement.shelf) ?? new Set<number>();
      for (const bay of covered) onShelf.add(bay);
      baysByShelf.set(placement.shelf, onShelf);
    }
  }
  if (baysByShelf.size === 0) return null;

  const only = runsByIndex.get([...touched][0]);
  const everyShelf =
    !!only && only.shelves.length > 0 && only.shelves.every((shelf) => baysByShelf.has(shelf.rank));
  const everyBay =
    !!only && only.bays > 0 && [...baysByShelf.values()].every((bays) => bays.size >= only.bays);
  if (everyShelf && everyBay) return null;

  return {
    cells: [...baysByShelf.entries()]
      .sort(([a], [b]) => a - b)
      .map(([shelf, bays]) => ({ shelf, bays: [...bays].sort((a, b) => a - b) })),
  };
};

const compactRanges = (values: readonly number[]): string => {
  const parts: string[] = [];
  let start = values[0];
  let previous = values[0];
  for (const value of values.slice(1)) {
    if (value === previous + 1) {
      previous = value;
      continue;
    }
    parts.push(start === previous ? `${start}` : `${start}-${previous}`);
    start = value;
    previous = value;
  }
  if (start !== undefined) parts.push(start === previous ? `${start}` : `${start}-${previous}`);
  return parts.join(" | ");
};

export const formatRestrictionPlacement = (placement: RestrictionPlacement | null): string => {
  if (!placement) return "not determinable — not uploaded";
  return placement.cells
    .map(
      (cell) =>
        `shelf ${cell.shelf} ${cell.bays.length === 1 ? "bay" : "bays"} ${compactRanges(cell.bays)}`,
    )
    .join("; ");
};

export const cellCount = (placement: RestrictionPlacement | null): number =>
  placement ? placement.cells.reduce((total, cell) => total + cell.bays.length, 0) : 0;
